// deps
import { useMemo } from "react";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { SplitPayMethodValues } from "@payment-client/interfaces/SplitPay";
import { SplitPayData } from "@swiggy-private/payment-ui";
import { PAYMENT_CODES } from "@swiggy-private/payment-types";

// hooks
import { useSessionStorage } from "@swiggy-private/checkout-core";
import { useEffectOnce, isEmpty } from "@swiggy-private/core-ui";

// helpers
import {
    getSelectedSplitPayMethods,
    isAnySplitPayMethodApplicableWithCoupon,
    isAnySplitPayMethodEnabled,
} from "@payment-client/helpers/SplitPay";

interface SplitpayStateReturnType {
    /** tells whether to display splitpay component or not */
    showSplitPay: boolean;
    /** tells whether to check/uncheck splitpay */
    withSplitPay: boolean;
    /** sets selected splitpay method list state */
    setSplitPayMethods: (paymentCodeList: string) => void;
    /** tells the selected split pay methods */
    splitPayMethods: string;
    /** processed split pay list with additional eligibility checks */
    splitPayList: SplitPayData[];
}

const useSplitPayState = (
    paymentResponse: GetPaymentMethodsResponse,
    withCoupon: boolean,
    availableUpiIntentMethods: string[] = [],
): SplitpayStateReturnType => {
    const splitPayList = useMemo<SplitPayData[]>(() => {
        const splitPayList = paymentResponse?.splitpayList;
        if (isEmpty(splitPayList)) {
            return splitPayList ?? [];
        }

        const availableUpiSet = new Set(availableUpiIntentMethods);
        let hasUpdates = false;

        const updatedSplitPayList = splitPayList.map((splitPayItem) => {
            if (splitPayItem.paymentCode !== PAYMENT_CODES.TWID_REWARD || !splitPayItem.enabled) {
                return splitPayItem;
            }

            const upiApp = splitPayItem?.meta?.eligibleApp;

            if (!upiApp) {
                return splitPayItem;
            }

            const isUpiAppAvailable = availableUpiSet.has(upiApp);

            hasUpdates = true;

            return {
                ...splitPayItem,
                upiAppUnavailable: !isUpiAppAvailable,
            };
        });
        return hasUpdates ? updatedSplitPayList : splitPayList;
    }, [paymentResponse?.splitpayList, availableUpiIntentMethods]);

    const selectedSplitPayList = getSelectedSplitPayMethods(splitPayList, SplitPayMethodValues.DEFAULT);
    const [splitPayMethods, setSplitPayMethods] = useSessionStorage("splitpay_method", selectedSplitPayList);

    useEffectOnce(() => {
        /*
         * UseSessionStorage on above line does not work on-load since it only extracts the value if present,
         * and does nothing when value is not present. This can lead to usage of a stale value, which is not in parity with current GPO response
         * This can happen very frequently, now that with TurboCheckoutM2. We are re-using GPO response of cart page on payment page also
         * */
        setSplitPayMethods(selectedSplitPayList);
    });

    const showSplitPay = useMemo(() => {
        const isNoSplitpayMethodEnabled = !isAnySplitPayMethodEnabled(splitPayList);
        const isNoSplitpayMethodApplicableWithCoupon = !isAnySplitPayMethodApplicableWithCoupon(splitPayList);

        if (isNoSplitpayMethodEnabled) {
            return false;
        }

        if (withCoupon && isNoSplitpayMethodApplicableWithCoupon) {
            return false;
        }

        return true;
    }, [splitPayList, withCoupon]);

    return {
        withSplitPay: !isEmpty(splitPayMethods) && splitPayMethods !== SplitPayMethodValues.DEFAULT,
        showSplitPay,
        splitPayMethods,
        setSplitPayMethods,
        splitPayList,
    };
};

export default useSplitPayState;
